import clsx from 'clsx';
import React, { FC } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState
} from 'react-accessible-accordion';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import { DropdownItemType, MultiDropdown } from 'types/common';

import { isNumeric } from '@/lib/util';

import Item from './Item';
import styles from './MultiDropdownItem.module.scss';
import YearsSelector from './YearsSelector';

interface MultiDropdownItemProps {
  block: MultiDropdown;
  onSelect?: (item: DropdownItemType) => DropdownItemType;
  id?: number;
  itemsCount?: number;
  selectedFiltersIds?: string[];
  setMenuFilters?: (filters, action?) => void;
  onSelectAllNoneCallback?: () => void;
}

const isSelectedMenuItem = (selectedFilters, menuFilters) => {
  let isIncluded = true;
  menuFilters.forEach((f) => {
    if (selectedFilters.includes(f.id)) {
      isIncluded = false;
      return;
    }
  });
  return isIncluded;
};

const currentYear = new Date().getFullYear();

const MultiDropdownItem: FC<MultiDropdownItemProps> = ({
  block,
  onSelect,
  id,
  itemsCount,
  selectedFiltersIds,
  setMenuFilters,
  onSelectAllNoneCallback
}) => {
  const { itemProps, buttonProps } = useDropdownMenu(block?.items?.length);
  const yearFilters = selectedFiltersIds.filter((year) => isNumeric(year)) || [];
  const yearFilterToggle =
    yearFilters.length === 0
      ? true
      : !(
          yearFilters[0] === block?.items[block.items.length - 1].id &&
          yearFilters[1] === String(currentYear)
        );

  const filterButtonToggle =
    block.component === 'yearsSelector'
      ? yearFilterToggle
      : isSelectedMenuItem(selectedFiltersIds, block?.items);
  const { onClick, ...buttonPropsRest } = buttonProps;

  const handleSelectNone = () => {
    if (block.component === 'yearsSelector' && block?.items?.length > 0) {
      const current = block.items[0];
      setMenuFilters([current, current], 'year');
    } else {
      const selectedFilters = block?.items?.filter((e) => selectedFiltersIds?.includes(e.id));
      setMenuFilters(selectedFilters, 'remove');
    }

    onSelectAllNoneCallback && onSelectAllNoneCallback();
  };

  const handleSelectAll = () => {
    if (block.component === 'yearsSelector' && block?.items?.length > 0) {
      setMenuFilters([block.items[block.items.length - 1], block.items[0]], 'year');
    } else {
      setMenuFilters(block?.items, 'add');
    }

    onSelectAllNoneCallback && onSelectAllNoneCallback();
  };

  if (!block.items?.length) return null;

  return (
    <>
      <div
        // key={id}
        className={clsx(styles.DropdownListWrapper, {
          [styles['DropdownListWrapper__right-border']]: id < itemsCount - 1
        })}
      >
        <div className={styles.DropdownListTitle}>
          <p>{block.title}</p>
          {block.component === 'menuItem' && (
            <button
              className={styles.FilterButton}
              type="button"
              onClick={() => {
                if (filterButtonToggle) {
                  handleSelectAll();
                } else {
                  handleSelectNone();
                }
              }}
              {...buttonPropsRest}
            >
              {filterButtonToggle ? 'All' : 'None'}
            </button>
          )}

          {/* Add year select all/none functionality */}
          {block.component === 'yearsSelector' && (
            <button
              className={styles.FilterButton}
              type="button"
              onClick={() => {
                if (filterButtonToggle) {
                  handleSelectAll();
                } else {
                  handleSelectNone();
                }
              }}
              {...buttonPropsRest}
            >
              {filterButtonToggle ? 'All' : 'None'}
            </button>
          )}
        </div>
        <ul
          className={clsx(styles.DropdownList, {
            // [styles.DropdownListTwoColumn]: block.items.length > 7
            [styles['DropdownList__yearsSelector']]: block.component === 'yearsSelector'
          })}
        >
          {block.component === 'menuItem' &&
            block.items.map((item, index) => {
              return (
                <li
                  key={item.name}
                  className={clsx(styles.DropdownList__item, {
                    // [styles['DropdownItem__two-cols']]: index <= 7 && block.items.length > 7
                  })}
                >
                  <Item
                    item={item}
                    onSelect={onSelect}
                    active={selectedFiltersIds?.includes(item.id)}
                    {...itemProps[index]}
                  />
                </li>
              );
            })}
          {block.component === 'yearsSelector' && (
            <YearsSelector
              years={block.items}
              onSelect={onSelect}
              startYear={Number(selectedFiltersIds?.filter((id) => isNumeric(id))?.[0]) || 2009}
              endYear={
                Number(selectedFiltersIds?.filter((id) => isNumeric(id))?.[1]) || currentYear
              }
            />
          )}
        </ul>
      </div>
      <Accordion
        // key={id}
        className={clsx(styles.DropdownListWrapper, styles['DropdownListWrapper--mobile'], {
          [styles['DropdownListWrapper__right-border']]: id < itemsCount - 1,
          [styles['DropdownListWrapper__top-border']]: id === 0
        })}
        allowZeroExpanded
      >
        <AccordionItem>
          <AccordionItemState>
            {(state) => (
              <AccordionItemHeading
                className={clsx(styles.DropdownListTitle, {
                  [styles['DropdownListTitle--active']]: state.expanded
                })}
              >
                <AccordionItemButton className={styles.Accordion__Button}>
                  <div className={styles.Accordion__ButtonContent}>
                    <p>{block.title}</p>
                    <button
                      className={styles.FilterButton}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (filterButtonToggle) {
                          handleSelectAll();
                        } else {
                          handleSelectNone();
                        }
                      }}
                      {...buttonPropsRest}
                    >
                      {filterButtonToggle ? 'All' : 'None'}
                    </button>
                  </div>
                </AccordionItemButton>
              </AccordionItemHeading>
            )}
          </AccordionItemState>
          <AccordionItemPanel>
            <ul className={clsx(styles.DropdownList, {})}>
              {block.component === 'menuItem' &&
                block.items.map((item, index) => {
                  return (
                    <li key={item.name} className={clsx(styles.DropdownList__item, {})}>
                      <Item
                        item={item}
                        onSelect={onSelect}
                        active={selectedFiltersIds?.includes(item.id)}
                        {...itemProps[index]}
                      />
                    </li>
                  );
                })}
              {block.component === 'yearsSelector' && (
                <li className={styles.DropdownItem__yearSelectorMobile}>
                  <YearsSelector
                    years={block.items}
                    onSelect={onSelect}
                    startYear={
                      Number(selectedFiltersIds?.filter((id) => isNumeric(id))?.[0]) || 2009
                    }
                    endYear={
                      Number(selectedFiltersIds?.filter((id) => isNumeric(id))?.[1]) || currentYear
                    }
                    mobile
                  />
                </li>
              )}
            </ul>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export default MultiDropdownItem;
